import { useSamlLogin, useReloadLicenseData } from '@/http/queries';
import { LoadingButton } from '@mui/lab';
import { IconBrandWindows } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogLevel } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/system';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
    auth: {
        clientId: '0b7a7198-cc6a-471e-a451-eeab4cf508b9',
        authority: 'https://login.microsoftonline.com/common',
        redirectUri: `${window.location.origin}/authentication/login`,
    },
    // cache: {
    //     cacheLocation: "sessionStorage", // This configures where your cache will be stored
    //     storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    // },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
    cache: {
        cacheLocation: 'localStorage', // Cache tokens in localStorage
        storeAuthStateInCookie: true, // To support older browsers
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
const loginRequest = {
    scopes: ['User.Read'],
};

const Container = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    // border: '1px solid #e0e0e0',
    // padding: theme.spacing(1),
    width: '100%',
    // maxWidth: '360px',
    margin: 'auto',
    textAlign: 'center',
}));

const LoginButton = styled(Button)(({ theme }) => ({
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'center',
    'backgroundColor': '#ffffff',
    'border': '1px solid #d3d3d3',
    'borderRadius': '8px',
    'padding': theme.spacing(1.5),
    'width': '100%',
    'textTransform': 'none',
    'boxShadow': '0 2px 4px rgba(0, 0, 0, 0.1)',
    'fontSize': '14px',
    'fontWeight': 'bold',
    'color': '#000000',
    '&:hover': {
        backgroundColor: '#f9f9f9',
    },
}));

const MicrosoftLogo = styled('img')(({ theme }) => ({
    height: '24px',
    width: '24px',
    marginRight: theme.spacing(1.5),
}));

export default function SAML() {
    const [error, setError] = useState(null); // Error state
    const { t } = useTranslation();
    const loginMutation = useSamlLogin(); // Use the custom hook directly here
    const reloadLicenseData = useReloadLicenseData();
    const { instance, accounts } = useMsal();
    const [organisationNotSupportedError, setOrganisationNotSupportedError] = useState(false);
    const [somethingWentWrongErr, setSomethingWentWrongErr] = useState(false);

    //TODO we will use it after successfully integrating login
    const handleLogin = async token => {
        console.log('saml login handle click');
        // e?.preventDefault();
        // setLoading(true); // Start loading
        try {
            const user = await loginMutation.mutateAsync({
                token,
            });

            console.log('Login successful', user);
            // Perform additional actions
            document.body.style.overflow = 'hidden';
            document.querySelector('#boot-screen').style.display = 'flex';
            await reloadLicenseData();
        } catch (e) {
            const errorMessage = `${e}`;
            console.log('error message from backend : ', errorMessage);
            setError(errorMessage); // Handle error
            if (errorMessage.includes('Organisation Not Found')) {
                setOrganisationNotSupportedError(true);
            } else if (!errorMessage.includes('User Not Found')) {
                console.log('checking user not found err condition');
                setSomethingWentWrongErr(true);
            }
        }
        // finally {
        //     setLoading(false); // Reset loading
        // }
    };

    // Through this function we will popup a new screen and take the credentials from user
    const handleClick = async () => {
        try {
            const loginResponse = await instance.loginPopup(loginRequest);
            fetchAccessToken(loginResponse);
        } catch (error) {
            console.error('Login error:', error);
        }
    };
    const fetchAccessToken = async loginResponse => {
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: loginResponse.account, // Use the first account from MSAL
            });
            handleLogin(tokenResponse.idToken); // Call handleLogin with the token
        } catch (silentError) {
            try {
                const tokenResponse = await instance.acquireTokenPopup(loginRequest);
                handleLogin(tokenResponse.idToken); // Call handleLogin with the token
            } catch (popupError) {
                console.error('Token acquisition failed:', popupError);
            }
        }
    };

    return (
        <Container>
            <>
                {error ? (
                    <Typography
                        variant='h3'
                        sx={{
                            color: '#b60000',
                            mt: 4,
                            mb: 2,
                        }}
                    >
                        {organisationNotSupportedError
                            ? t('OrganisationNotSupported')
                            : somethingWentWrongErr
                              ? t('errorTryAgain')
                              : t('UserNotFound')}
                    </Typography>
                ) : null}
            </>

            <LoginButton onClick={handleClick}>
                <MicrosoftLogo
                    src='https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg' // Microsoft logo
                    alt='Microsoft Logo'
                />
                {t('LogInWithMicrosoft')}
            </LoginButton>
        </Container>
    );
}
